import { ElementState } from '@src/renderer/ElementState';
import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { useVideoCreatorStore } from '@src/stores-v2';
import { ExtraElementData } from '@src/types.ts/story';
import { mapToElementState } from '@src/videoTranscriptionProcessor/utils';

type ReturnType = {
  hiddenParentElement: ElementState | null;
  isLayoutComposition: boolean;
  childVideoElement: ElementState | null;
  displayElement: ElementState;
  sourceUrl: string;
  xValue: number;
  yValue: number;
  modifyPropertyWithSlider: (propertyName: string, newValue: number) => void;
  handleChangeScale: (value: number) => void;
};

export default function useLayoutAwareVideoSettingsState({
  activeElement,
}: {
  activeElement: ElementState;
}): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const videoSourceStore = videoCreator.rootStore.videoSourceStore;

  const [hiddenParentElement, setHiddenParentElement] =
    useState<ElementState | null>(null);
  const [isLayoutComposition, setIsLayoutComposition] =
    useState<boolean>(false);
  const [childVideoElement, setChildVideoElement] =
    useState<ElementState | null>(null);
  // Keep track of slider values to prevent UI from snapping
  const [sliderValues, setSliderValues] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  // Function to update slider values from the current element properties
  // Using a function in the setState to avoid dependency on sliderValues
  const updateSliderValuesFromElement = useCallback(() => {
    const element = childVideoElement || activeElement;
    if (element && element.source) {
      setSliderValues((prevValues) => {
        const newValues = { ...prevValues };

        // For X alignment, prefer x_alignment if available, then x
        if (element.source.x_alignment !== undefined) {
          const xString = element.source.x_alignment.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else if (element.source.x !== undefined) {
          const xString = element.source.x.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else {
          newValues.x = 50; // Default value
        }

        // For Y alignment, prefer y_alignment if available, then y
        if (element.source.y_alignment !== undefined) {
          const yString = element.source.y_alignment.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else if (element.source.y !== undefined) {
          const yString = element.source.y.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else {
          newValues.y = 50; // Default value
        }

        return newValues;
      });
    }
  }, [(childVideoElement || activeElement)?.source]);

  // Update slider values when the child/active video element changes
  useEffect(() => {
    updateSliderValuesFromElement();
  }, [updateSliderValuesFromElement]);

  // Modified function to immediately update the UI and then debounce the API call
  const modifyPropertyWithSlider = (propertyName: string, newValue: number) => {
    // Update local state immediately for smooth UI
    if (propertyName === 'x') {
      setSliderValues((prev) => ({ ...prev, x: newValue }));
    } else if (propertyName === 'y') {
      setSliderValues((prev) => ({ ...prev, y: newValue }));
    }

    // Convert to percentage string (without spaces)
    const propertyValue = `${newValue}%`;

    // Debounce the actual API call
    modifyPropertyDebounced(propertyName, propertyValue);
  };

  const modifyPropertyDebounced = useCallback(
    debounce(async (propertyName: string, propertyValue: any) => {
      const actionLabel = `changing video ${propertyName}`;
      const elementToModify = childVideoElement || activeElement;
      const modifications: Record<string, string> = {};

      // If changing x or y alignment, also update the related properties to match
      if (propertyName === 'x') {
        // Set all related x-position values with percentage strings to ensure they stay in sync
        modifications[`${elementToModify?.source.id}.x_anchor`] = propertyValue;
        modifications[`${elementToModify?.source.id}.x`] = propertyValue;
        modifications[`${elementToModify?.source.id}.x_alignment`] =
          propertyValue;
      } else if (propertyName === 'y') {
        // Set all related y-position values with percentage strings to ensure they stay in sync
        modifications[`${elementToModify?.source.id}.y_anchor`] = propertyValue;
        modifications[`${elementToModify?.source.id}.y`] = propertyValue;
        modifications[`${elementToModify?.source.id}.y_alignment`] =
          propertyValue;
      } else {
        // For other properties that don't need syncing
        modifications[`${elementToModify?.source.id}.${propertyName}`] =
          propertyValue;
      }

      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        actionLabel,
      );
    }, 250),
    [childVideoElement, activeElement, videoCreator],
  );

  const handleChangeScale = debounce(async (value: number) => {
    const actionLabel = 'changing video scale';
    const scale = value + '%';
    const elementToModify = childVideoElement || activeElement;
    await videoCreator.applyVideoStateModifications(
      {
        [`${elementToModify?.source.id}.${'width'}`]: scale,
        [`${elementToModify?.source.id}.${'height'}`]: scale,
      },
      true,
      actionLabel,
    );
  }, 250);

  useEffect(() => {
    // Check if current element is a composition element part of a layout
    const elementId = activeElement.source.id;
    const eed = videoCreator.currentVideo?.extraElementData[
      elementId
    ] as ExtraElementData | null;

    // Handle composition elements
    if (activeElement.source.type === 'composition') {
      // Find the child video element inside this composition
      if (activeElement.elements && activeElement.elements.length > 0) {
        const videoChild = activeElement.elements.find(
          (e) => e.source.type === 'video',
        );
        if (videoChild) {
          setChildVideoElement(videoChild);
        }
      }

      // Check if it's a layout composition
      if (
        eed &&
        eed.layoutPosition &&
        eed.layoutPosition !== 'hidden' &&
        eed.layoutAssociatedIds &&
        eed.layoutAssociatedIds.length > 0
      ) {
        setIsLayoutComposition(true);

        // This is a layout composition (top or bottom), get the hidden parent element
        const hiddenParentId = eed.layoutAssociatedIds[0];
        const hiddenParent = videoSourceStore.source.elements.find(
          (e: Record<string, any>) => e.id === hiddenParentId,
        );

        if (hiddenParent) {
          setHiddenParentElement(
            mapToElementState(hiddenParent) as ElementState,
          );
        }
      } else {
        setIsLayoutComposition(false);
        setHiddenParentElement(null);
      }
    } else {
      // Handle direct video elements (not compositions)
      setIsLayoutComposition(false);
      setHiddenParentElement(null);
      setChildVideoElement(null);
    }
  }, [activeElement, videoCreator.currentVideo?.extraElementData]);

  // Determine which element to use for different properties
  const displayElement = childVideoElement || activeElement;

  // Update slider values directly based on current state
  // Do this in render to ensure it's always up-to-date
  const activeElementForSliders = childVideoElement || activeElement;

  // Set x value directly from the source
  const xValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.x != null) {
      return sliderValues.x;
    }

    // Try x_alignment first
    if (activeElementForSliders.source.x_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.x_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try x
    if (activeElementForSliders.source.x !== undefined) {
      const val = parseFloat(activeElementForSliders.source.x.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  // Set y value directly from the source
  const yValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.y != null) {
      return sliderValues.y;
    }

    // Try y_alignment first
    if (activeElementForSliders.source.y_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.y_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try y
    if (activeElementForSliders.source.y !== undefined) {
      const val = parseFloat(activeElementForSliders.source.y.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  const sourceUrl = displayElement.source.source;

  return {
    hiddenParentElement,
    isLayoutComposition,
    childVideoElement,
    displayElement,
    sourceUrl,
    xValue,
    yValue,
    modifyPropertyWithSlider,
    handleChangeScale,
  };
}
